import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Globe, Lock } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface ShareToggleProps {
  id: string
  isPublic: boolean
  type: "image" | "video" | "music" | "speech"
  prompt: string
  onToggle: (newState: boolean) => void
}

export function ShareToggle({ id, isPublic, type, prompt, onToggle }: ShareToggleProps) {
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const { toast } = useToast()

  const handleToggle = async () => {
    setLoading(true)
    try {
      const tableMap = {
        image: "images",
        video: "generated_videos",
        music: "generated_music",
        speech: "generated_speech"
      }
      const table = tableMap[type]
      const supabaseAny = supabase as any
      const { error } = await supabaseAny
        .from(table)
        .update({ is_public: !isPublic })
        .eq("id", id)

      if (error) throw error

      // For music: also update all variants that have this track as parent
      if (type === "music") {
        const { error: variantError } = await supabaseAny
          .from("generated_music")
          .update({ is_public: !isPublic })
          .eq("parent_generation_id", id)
        
        if (variantError) {
          console.error("Error updating music variants:", variantError)
          // Don't fail - parent was updated successfully
        }
      }

      // If making public, queue for delayed engagement boost
      if (!isPublic) {
        // Calculate random delay: 30 seconds to 10 minutes
        const minDelay = 30 * 1000 // 30 seconds
        const maxDelay = 10 * 60 * 1000 // 10 minutes
        const randomDelay = Math.floor(Math.random() * (maxDelay - minDelay)) + minDelay
        const boostAfter = new Date(Date.now() + randomDelay).toISOString()

        // Insert into pending_boosts table
        const { error: boostError } = await supabase
          .from('pending_boosts')
          .insert({
            content_type: type,
            content_id: id,
            prompt: prompt,
            boost_after: boostAfter,
          })

        if (boostError) {
          // Log but don't fail the toggle - content is already public
          console.error('Error queueing boost:', boostError)
        } else {
          console.log(`Queued boost for ${type}/${id} after ${Math.round(randomDelay / 1000)}s`)
        }
      }

      onToggle(!isPublic)
      toast({
        title: !isPublic ? "Made Public" : "Made Private",
        description: !isPublic 
          ? `Your ${type} is now visible to everyone in Explore` 
          : `Your ${type} is now private`,
      })
    } catch (error) {
      console.error("Error toggling visibility:", error)
      toast({
        title: "Error",
        description: `Failed to update ${type} visibility`,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
      setOpen(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          disabled={loading}
          className="h-8 w-8 p-0"
          title={isPublic ? "Public - Click to make private" : "Private - Click to make public"}
        >
          {isPublic ? (
            <Globe className="h-4 w-4 text-green-500" />
          ) : (
            <Lock className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isPublic ? "Make Private?" : "Make Public?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isPublic 
              ? `This ${type} will no longer be visible in the Explore gallery. Only you will be able to see it.`
              : `This ${type} will be visible to everyone in the Explore gallery. Anyone can view and download it.`
            }
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleToggle} disabled={loading}>
            {loading ? "Updating..." : isPublic ? "Make Private" : "Make Public"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
